/**
 * A line diff, for the cards that show what a request would write (AIT-51): an agent's instructions and every text file
 * of a skill. Myers' algorithm over lines after the common head and tail are set aside; a change too large to walk
 * (past MAX_EDITS) is drawn as the old text removed and the new one added, which is still exactly what changes.
 */
export type DiffLine = { op: "same" | "add" | "del"; text: string };

/** One run of lines to draw: changed lines with up to `context` unchanged ones around them, or a fold of the rest. */
export type DiffHunk = { kind: "lines"; lines: DiffLine[] } | { kind: "fold"; count: number };

const MAX_EDITS = 1000;

const splitLines = (text: string): string[] => (text === "" ? [] : text.replace(/\r\n/g, "\n").replace(/\n$/, "").split("\n"));

export function lineDiff(before: string, after: string): DiffLine[] {
  const a = splitLines(before);
  const b = splitLines(after);
  let head = 0;
  while (head < a.length && head < b.length && a[head] === b[head]) head++;
  let tail = 0;
  while (tail < a.length - head && tail < b.length - head && a[a.length - 1 - tail] === b[b.length - 1 - tail]) tail++;
  const same = (lines: string[]): DiffLine[] => lines.map((text) => ({ op: "same", text }));
  const middle = walk(a.slice(head, a.length - tail), b.slice(head, b.length - tail));
  return [...same(a.slice(0, head)), ...removedFirst(middle), ...same(a.slice(a.length - tail))];
}

/** Within each stretch of changed lines, what goes before what comes in: a replaced line reads old, then new. */
function removedFirst(lines: DiffLine[]): DiffLine[] {
  const out: DiffLine[] = [];
  let i = 0;
  while (i < lines.length) {
    if (lines[i]!.op === "same") {
      out.push(lines[i++]!);
      continue;
    }
    let j = i;
    while (j < lines.length && lines[j]!.op !== "same") j++;
    const run = lines.slice(i, j);
    out.push(...run.filter((l) => l.op === "del"), ...run.filter((l) => l.op === "add"));
    i = j;
  }
  return out;
}

/** Myers' shortest edit script between two lists of lines, read back from the saved frontiers. */
function walk(a: string[], b: string[]): DiffLine[] {
  const n = a.length;
  const m = b.length;
  if (!n) return b.map((text) => ({ op: "add", text }));
  if (!m) return a.map((text) => ({ op: "del", text }));
  const max = n + m;
  const offset = max;
  const v = new Int32Array(2 * max + 2);
  const trace: Int32Array[] = [];
  let found = -1;
  for (let d = 0; d <= Math.min(max, MAX_EDITS); d++) {
    // Only the diagonals the next step reads (k from -d-1 to d+1), so the saved frontiers stay small.
    trace.push(v.slice(offset - d - 1, offset + d + 2));
    for (let k = -d; k <= d; k += 2) {
      let x = k === -d || (k !== d && v[offset + k - 1]! < v[offset + k + 1]!) ? v[offset + k + 1]! : v[offset + k - 1]! + 1;
      let y = x - k;
      while (x < n && y < m && a[x] === b[y]) {
        x++;
        y++;
      }
      v[offset + k] = x;
      if (x >= n && y >= m) {
        found = d;
        break;
      }
    }
    if (found >= 0) break;
  }
  if (found < 0) return [...a.map((text): DiffLine => ({ op: "del", text })), ...b.map((text): DiffLine => ({ op: "add", text }))];
  const out: DiffLine[] = [];
  let x = n;
  let y = m;
  for (let d = found; d > 0; d--) {
    const prev = trace[d]!;
    const at = (k: number) => prev[k + d + 1]!;
    const k = x - y;
    const prevK = k === -d || (k !== d && at(k - 1) < at(k + 1)) ? k + 1 : k - 1;
    const prevX = at(prevK);
    const prevY = prevX - prevK;
    while (x > prevX && y > prevY) out.push({ op: "same", text: a[--x]! }), y--;
    if (x === prevX) out.push({ op: "add", text: b[--y]! });
    else out.push({ op: "del", text: a[--x]! });
  }
  while (x > 0 && y > 0) out.push({ op: "same", text: a[--x]! }), y--;
  return out.reverse();
}

/** The diff as it is drawn: every change with `context` lines either side, the long unchanged stretches folded. */
export function diffHunks(lines: DiffLine[], context = 3): DiffHunk[] {
  const keep = lines.map(() => false);
  lines.forEach((l, i) => {
    if (l.op === "same") return;
    for (let j = Math.max(0, i - context); j <= Math.min(lines.length - 1, i + context); j++) keep[j] = true;
  });
  const hunks: DiffHunk[] = [];
  let i = 0;
  while (i < lines.length) {
    const shown = keep[i];
    let j = i;
    while (j < lines.length && keep[j] === shown) j++;
    hunks.push(shown ? { kind: "lines", lines: lines.slice(i, j) } : { kind: "fold", count: j - i });
    i = j;
  }
  return hunks;
}

/** How many lines a diff adds and removes: the summary beside a file's name. */
export function diffCounts(lines: DiffLine[]): { added: number; removed: number } {
  return { added: lines.filter((l) => l.op === "add").length, removed: lines.filter((l) => l.op === "del").length };
}
